import { assets } from "@/lib/assets";
import { ArrowUpRight, BookOpen, Building2, Briefcase } from "lucide-react";

const divisions = [
  {
    number: "01",
    tag: "Consulting & Branding",
    title: "High-impact leadership development and strategic brand alignment.",
    description:
      "Professional development, workforce training, and brand strategy for institutions ready to lead with clarity, confidence, and purpose.",
    bullets: [
      "Executive Leadership Development",
      "Professional Development Programs",
      "Workforce Development Training",
      "Strategic Brand Alignment",
    ],
    image: assets.founderThroneNew,
    imageAlt: "Karlene, Founder — the royal-treatment standard for every engagement",
    icon: Briefcase,
    ctaLabel: "Talk to a Consultant",
    id: "division-consulting",
    brandLogo: null,
  },
  {
    number: "02",
    tag: "BOOKKAT Bookkeeping Solutions",
    title: "Precision bookkeeping for small businesses that mean business.",
    description:
      "From QuickBooks setup to monthly reconciliation, BOOKKAT keeps your financial house in order so you can spend your time growing revenue instead of chasing receipts.",
    bullets: [
      "QuickBooks Setup & Management",
      "Financial Organization for Small Businesses",
      "Monthly Reporting & Reconciliation",
      "Advisory & Cash-Flow Guidance",
    ],
    image: assets.bookkeeping,
    imageAlt: "Financial documents and laptop on a clean desk",
    icon: BookOpen,
    ctaLabel: "Organize My Books",
    id: "division-bookkeeping",
    brandLogo: assets.bookkatLogo,
  },
  {
    number: "03",
    tag: "BOOKKAT Property Solutions",
    title: "Short-term rental management that performs like an investment.",
    description:
      "Airbnb hosting, rental arbitrage operations, and short-term rental portfolios — designed to produce consistent income and set the stage for property ownership.",
    bullets: [
      "Air BnB Hosting",
      "Short-Term Rental",
      "Long-Term Rental",
      "Accommodation for Professionals",
    ],
    image: assets.property,
    imageAlt: "Modern short-term rental interior",
    icon: Building2,
    ctaLabel: "Explore Property Solutions",
    id: "division-property",
    brandLogo: assets.bookkatLogo,
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[color:var(--kat-cream)] border-y border-slate-200"
    >
      <div className="kat-container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24">
          <div className="max-w-2xl">
            <p className="overline">Three Divisions · One Firm</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[color:var(--kat-navy)]">
              Royal treatment for the Leaders & Operators we serve.
            </h2>
          </div>
          <p className="max-w-md text-base md:text-lg text-[color:var(--kat-muted)] leading-relaxed">
            Whether you need a thinking partner, a bookkeeping backbone, or a property
            operator, our divisions are built to work independently — and even better
            together.
          </p>
        </div>

        <div className="space-y-24 md:space-y-32">
          {divisions.map((d, i) => {
            const Icon = d.icon;
            const reverse = i % 2 === 1;
            return (
              <article
                id={d.id}
                key={d.id}
                data-testid={`division-card-${d.number}`}
                className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center"
              >
                {/* Image column */}
                <div
                  className={`lg:col-span-6 ${
                    reverse ? "lg:order-2" : "lg:order-1"
                  }`}
                >
                  <div className="relative aspect-[4/3] rounded-sm overflow-hidden border border-slate-200 group">
                    <img
                      src={d.image}
                      alt={d.imageAlt}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                    />
                    <div
                      className="absolute inset-0 bg-gradient-to-tr from-[color:var(--kat-navy)]/30 via-transparent to-transparent pointer-events-none"
                      aria-hidden
                    />
                    <div className="absolute top-6 left-6 flex items-center gap-3 bg-white/95 backdrop-blur px-4 py-2 rounded-sm border border-slate-200">
                      <Icon size={16} className="text-[color:var(--kat-navy)]" strokeWidth={1.6} />
                      <span className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[color:var(--kat-navy)]">
                        {d.tag}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content column */}
                <div
                  className={`lg:col-span-6 ${
                    reverse ? "lg:order-1" : "lg:order-2"
                  }`}
                >
                  {d.brandLogo && (
                    <div className="mb-6 flex items-center gap-3">
                      <img
                        src={d.brandLogo}
                        alt="BOOKKAT — a division of KAT Consulting & Branding"
                        data-testid={`division-brand-logo-${d.number}`}
                        className="h-14 w-14 md:h-16 md:w-16 object-contain rounded-sm bg-white border border-slate-200 p-1.5"
                      />
                      <span className="text-[11px] tracking-[0.28em] uppercase font-semibold text-[color:var(--kat-sky-deep)]">
                        A Division of KAT Consulting & Branding
                      </span>
                    </div>
                  )}
                  <div className="flex items-baseline gap-4 mb-6">
                    <span className="font-serif italic text-[color:var(--kat-sky-deep)] text-lg">
                      {d.number}
                    </span>
                    <span className="h-px flex-1 bg-slate-300" aria-hidden />
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl lg:text-[44px] leading-[1.08] tracking-tight text-[color:var(--kat-navy)]">
                    {d.title}
                  </h3>
                  <p className="mt-6 text-base md:text-lg text-[color:var(--kat-muted)] leading-relaxed max-w-xl">
                    {d.description}
                  </p>
                  <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                    {d.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-sm text-[color:var(--kat-navy)]"
                      >
                        <span
                          className="mt-2 w-1.5 h-1.5 rounded-full bg-[color:var(--kat-sky-deep)] shrink-0"
                          aria-hidden
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#contact"
                    data-testid={`division-cta-${d.number}`}
                    className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-[color:var(--kat-navy)] border-b border-[color:var(--kat-navy)] pb-1 hover:text-[color:var(--kat-sky-deep)] hover:border-[color:var(--kat-sky-deep)] transition-colors"
                  >
                    {d.ctaLabel}
                    <ArrowUpRight size={16} />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
